/**
 * V2StreamOverlay — streaming progress HUD components
 * Phase 5 (PRI-291): extracted from PrimateVisionV2.tsx
 *
 * Contains: ProgressBlock (offline inference progress), StreamProgressTray (live stream setup stages)
 */

import { L } from './pv2-shared'

// ─────────────────────────────────────────────────────────────────────────────
// Progress Block - shown inline in thread area during inference
// ─────────────────────────────────────────────────────────────────────────────

export function ProgressBlock({
  step, processingText, isMobile = false, samEnabled = false,
  jobProgress, completingSteps, dismissing = false,
}: {
  step: number;
  processingText?: string;
  isMobile?: boolean;
  samEnabled?: boolean;
  jobProgress?: { stage: string; pct: number; message: string } | null;
  completingSteps?: boolean;
  dismissing?: boolean;
}) {
  const stages = samEnabled
    ? ['Upload', 'JEPA', 'Segment', 'Render', 'Complete']
    : ['Upload', 'JEPA', 'Render', 'Complete'];

  return (
    <>
      <style>{`
        @keyframes pv2-progress { 0%{width:5%} 50%{width:75%} 100%{width:5%} }
        @keyframes pv2-tray-in  { from{opacity:0;transform:translateX(24px) scale(0.97)} to{opacity:1;transform:translateX(0) scale(1)} }
        @keyframes pv2-tray-out { from{opacity:1;transform:translateX(0) scale(1)} to{opacity:0;transform:translateX(24px) scale(0.97)} }
      `}</style>
      <div style={{
        ...(isMobile ? {
          width: '100%',
          maxWidth: 400,
        } : {
          position: 'absolute' as const,
          /* Sit directly above the input bar, aligned to its right edge */
          bottom: 'calc(100% + 10px)',
          right: 0,
          width: 300,
        }),
        background: L.bgSurface,
        border: `1px solid ${L.border}`,
        borderRadius: 14,
        padding: '18px 20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
        zIndex: 50,
        animation: dismissing ? 'pv2-tray-out 400ms ease both' : 'pv2-tray-in 180ms ease both',
      }}>
        {/* Connector pip - hidden on mobile */}
        {!isMobile && (
          <div style={{
            position: 'absolute', bottom: -6, right: 20,
            width: 10, height: 10,
            background: L.bgSurface,
            border: `1px solid ${L.border}`,
            transform: 'rotate(45deg)',
            borderTop: 'none', borderLeft: 'none',
          }} />
        )}

        <div style={{
          fontSize: 10, fontFamily: '"JetBrains Mono", monospace',
          color: L.textMuted, marginBottom: 14,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          {processingText ?? 'Running inference...'}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {stages.map((stage, i) => {
            const done = i < step;
            const active = i === step;
            const isCompleteFlash = completingSteps && stage === 'Complete' && active;
            const dotColor = done || isCompleteFlash ? (isCompleteFlash ? '#16a34a' : L.signal) : active ? `${L.signal}18` : L.bgCode;
            return (
              <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Status dot */}
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: dotColor,
                  border: active && !isCompleteFlash ? `1.5px solid ${L.signal}` : isCompleteFlash ? '1.5px solid #16a34a' : 'none',
                  transition: 'all 300ms',
                }}>
                  {done ? (
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5l2.5 2.5L8.5 2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : isCompleteFlash ? (
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5l2.5 2.5L8.5 2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : active ? (
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: L.signal }} />
                  ) : null}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 12,
                    color: done ? L.textMuted : active ? L.textPrimary : L.textMuted,
                    fontWeight: active ? 600 : 400,
                    lineHeight: 1,
                  }}>
                    {stage}
                  </div>
                  {active && !isCompleteFlash && (
                    <>
                      <div style={{ marginTop: 5, height: 2, borderRadius: 2, background: L.bgCode, overflow: 'hidden' }}>
                        {jobProgress?.pct != null ? (
                          <div style={{
                            height: '100%', background: L.signal, borderRadius: 2,
                            width: `${jobProgress.pct}%`, transition: 'width 0.5s ease',
                          }} />
                        ) : (
                          <div style={{ height: '100%', background: L.signal, animation: 'pv2-progress 2.5s ease-in-out infinite', borderRadius: 2 }} />
                        )}
                      </div>
                      {jobProgress?.message && (
                        <div style={{ marginTop: 3, fontSize: 10, color: L.textMuted, lineHeight: 1.3 }}>
                          {jobProgress.message}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stream Progress Tray - floating above input bar, mirrors ProgressBlock layout
// ─────────────────────────────────────────────────────────────────────────────

// Stream setup stages - shown between Submit and first annotated frame.
// Dynamic: 'queued' row only appears when the session is actually queued.
export function StreamProgressTray({ connectionState, prompt, queue, isMobile = false, dismissing = false, connectProgress = 0, onCancel }: {
  /* Inject keyframes - StreamProgressTray may render without ProgressBlock */
  /* (ProgressBlock is only mounted during offline processing) */
  connectionState: string;
  prompt: string;
  queue: { position: number; estimated_wait_s: number } | null;
  isMobile?: boolean;
  dismissing?: boolean;
  connectProgress?: number;
  /** Optional cancel handler — renders a Cancel button at the bottom of the tray when provided */
  onCancel?: () => void;
}) {
  // Build stages dynamically - hide Queued unless actually queued (or was queued)
  const wasQueued = connectionState === 'queued' || (queue != null && queue.position > 0);
  const stages: Array<{ key: string; label: string; subtext?: string }> = [
    { key: 'connecting', label: 'Connecting' },
    ...(wasQueued ? [{ key: 'queued', label: 'Queued', subtext: queue ? `Position ${queue.position}${queue.estimated_wait_s > 0 ? ` · ~${queue.estimated_wait_s}s wait` : ''}` : undefined }] : []),
    { key: 'buffering', label: 'Starting inference' },
  ];

  // Map connectionState to active stage index
  let effectiveIdx = 0;
  if (connectionState === 'connecting') {
    effectiveIdx = 0;
  } else if (connectionState === 'queued') {
    effectiveIdx = stages.findIndex(s => s.key === 'queued');
  } else if (connectionState === 'buffering') {
    effectiveIdx = stages.findIndex(s => s.key === 'buffering');
  } else if (['live', 'degraded', 'quota_warning'].includes(connectionState)) {
    // All stages done - tray should be dismissing
    effectiveIdx = stages.length;
  }
  if (effectiveIdx < 0) effectiveIdx = 0;

  const headerText = connectionState === 'queued'
    ? 'Waiting for GPU...'
    : connectionState === 'buffering'
    ? 'Starting inference...'
    : 'Setting up stream...';

  return (
    <>
    <style>{`
      @keyframes pv2-progress { 0%{width:5%} 50%{width:75%} 100%{width:5%} }
      @keyframes pv2-tray-in  { from{opacity:0;transform:translateX(24px) scale(0.97)} to{opacity:1;transform:translateX(0) scale(1)} }
      @keyframes pv2-tray-out { from{opacity:1;transform:translateX(0) scale(1)} to{opacity:0;transform:translateX(24px) scale(0.97)} }
    `}</style>
    <div data-testid="stream-progress-tray" style={{
      ...(isMobile ? {
        width: '100%',
        maxWidth: 400,
      } : {
        position: 'absolute' as const,
        bottom: 'calc(100% + 10px)',
        right: 0,
        width: 300,
      }),
      background: L.bgSurface,
      border: `1px solid ${L.border}`,
      borderRadius: 14,
      padding: '18px 20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
      zIndex: 50,
      animation: dismissing ? 'pv2-tray-out 400ms ease both' : 'pv2-tray-in 180ms ease both',
    }}>
      {/* Connector pip - hidden on mobile */}
      {!isMobile && (
        <div style={{
          position: 'absolute', bottom: -6, right: 20,
          width: 10, height: 10,
          background: L.bgSurface,
          border: `1px solid ${L.border}`,
          transform: 'rotate(45deg)',
          borderTop: 'none', borderLeft: 'none',
        }} />
      )}

      <div style={{
        fontSize: 10, fontFamily: '"JetBrains Mono", monospace',
        color: L.textMuted, marginBottom: 14,
        letterSpacing: '0.1em', textTransform: 'uppercase' as const,
      }}>
        {headerText}
        {prompt && (
          <span style={{ marginLeft: 6, color: L.textMuted, fontStyle: 'italic', textTransform: 'none' as const }}>
            "{prompt.length > 40 ? prompt.slice(0, 40) + '...' : prompt}"
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {stages.map((stage, i) => {
          const done = i < effectiveIdx;
          const active = i === effectiveIdx;
          return (
            <div key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? L.signal : active ? `${L.signal}18` : L.bgCode,
                border: active ? `1.5px solid ${L.signal}` : 'none',
                transition: 'all 300ms',
              }}>
                {done ? (
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                    <path d="M1.5 5l2.5 2.5L8.5 2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : active ? (
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: L.signal }} />
                ) : null}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 12,
                  color: done ? L.textMuted : active ? L.textPrimary : L.textMuted,
                  fontWeight: active ? 600 : 400,
                  lineHeight: 1,
                }}>
                  {stage.label}
                </div>
                {active && (
                  <>
                    <div style={{ marginTop: 5, height: 2, borderRadius: 2, background: L.bgCode, overflow: 'hidden' }}>
                      {stage.key === 'connecting' ? (
                        /* Real progress: 4 sub-steps (TURN→WS→Auth→WebRTC) = 25% each */
                        <div style={{
                          height: '100%', background: L.signal, borderRadius: 2,
                          width: `${Math.min(connectProgress * 25, 100)}%`,
                          transition: 'width 300ms ease',
                        }} />
                      ) : (
                        /* Infinite loop for stages without measurable progress */
                        <div style={{ height: '100%', background: L.signal, animation: 'pv2-progress 2.5s ease-in-out infinite', borderRadius: 2 }} />
                      )}
                    </div>
                    {stage.subtext && (
                      <div style={{ marginTop: 3, fontSize: 10, color: L.textMuted, lineHeight: 1.3 }}>
                        {stage.subtext}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Cancel button — only rendered when caller provides onCancel handler */}
      {onCancel && (
        <div style={{ marginTop: 14, display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end' }}>
          <button
            onClick={onCancel}
            data-testid="stream-cancel-btn"
            style={{
              minWidth: 44,
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
              background: 'transparent',
              border: `1px solid ${L.border}`,
              borderRadius: 8,
              color: L.textMuted,
              fontSize: 12,
              fontFamily: '"JetBrains Mono", monospace',
              cursor: 'pointer',
              letterSpacing: '0.04em',
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stream Error Tray — shared (desktop + mobile) error narrative shown when a
// live stream fails. Centralizes the auth-aware message classification so the
// user always gets a descriptive reason + how to resolve it, instead of a
// silent "Starting inference..." hang. (2026-06-11 investor-onboarding fix.)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRI-414: exact server error codes that classifyStreamError maps to a
 * friendly narrative. onError handlers route these codes straight through to
 * setErrorMessage (via streamErrorDisplayInput) instead of the server's
 * message string, so the exact-code matches below actually fire.
 */
export const EXACT_STREAM_ERROR_CODES: ReadonlySet<string> = new Set([
  'auth_timeout',
  'queue_full',
  'too_many_connections',
  'duplicate_session',
  'terms_not_accepted',
  'no_inference_server',
  'not_ready',
  // PRI-413/414: sidecar rejection + prompt-pending admission timeout
  'slot_busy',
  'sidecar_error',
  'prompt_timeout',
  // PRI-424: mid-stream server-side death (e.g. sidecar crash) — the API ends
  // the session with reason=server_error after its 5s reconnect grace window.
  'server_error',
  // auth family (substring/exact-handled below)
  'access_restricted',
  'unauthorized',
  'forbidden',
])

/**
 * PRI-414: pick what to feed classifyStreamError from an onError(code, message)
 * pair. Known codes route through as-is (classifier owns the narrative);
 * unknown codes fall back to the server message, then the code, then a generic
 * "Connection lost" — never an empty string (which rendered as a silent black
 * RE-RUN STREAM card).
 */
export function streamErrorDisplayInput(
  code: string | null | undefined,
  message?: string | null,
): string {
  if (code && EXACT_STREAM_ERROR_CODES.has(code)) return code
  if (message && message.trim()) return message
  if (code && code.trim()) return code
  return 'Connection lost'
}

/**
 * Classify a raw stream error message into a user-facing narrative + flags.
 * Exported so unit tests can assert the mapping without rendering.
 */
export function classifyStreamError(rawMsg: string): {
  displayMsg: string;
  isAccessRestricted: boolean;
  isUnauthorized: boolean;
  /** No-first-frame watchdog timeout — typically an auth/authorization problem. */
  isFirstFrameTimeout: boolean;
  /** Whether a Retry button makes sense (transient errors only). */
  retryable: boolean;
} {
  // Exact error-code matches (server sends these as raw codes via onError).
  // Handle before substring matching to avoid partial collisions.
  if (rawMsg === 'auth_timeout') {
    return { displayMsg: 'Couldn\u2019t establish a secure session. Check your connection and try again.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: true };
  }
  if (rawMsg === 'queue_full') {
    return { displayMsg: 'All inference slots are busy right now. Please try again in a few minutes.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: true };
  }
  if (rawMsg === 'too_many_connections') {
    return { displayMsg: 'Too many connections from your network. Close other sessions and retry.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: false };
  }
  if (rawMsg === 'duplicate_session') {
    return { displayMsg: 'You already have a live session open in another tab. Close it and retry.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: false };
  }
  if (rawMsg === 'terms_not_accepted') {
    return { displayMsg: 'Please accept the Terms of Use, then start again.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: false };
  }
  if (rawMsg === 'no_inference_server') {
    return { displayMsg: 'We couldn\u2019t reach the inference engine \u2014 our team has been alerted. Please retry in a moment.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: true };
  }
  if (rawMsg === 'not_ready') {
    return { displayMsg: 'Still getting things ready \u2014 please retry in a moment.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: true };
  }
  // PRI-414: terminal codes surfaced by PRI-413's sidecar-rejection handling.
  if (rawMsg === 'slot_busy') {
    return { displayMsg: 'All streaming slots are busy right now \u2014 please try again in a moment.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: true };
  }
  if (rawMsg === 'sidecar_error') {
    return { displayMsg: 'The streaming server rejected this session. Please try again.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: true };
  }
  if (rawMsg === 'prompt_timeout') {
    return { displayMsg: 'Session expired \u2014 no prompt was submitted in time, so the streaming slot was released. Submit your prompt to start again.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: true };
  }
  // PRI-424: the stream died server-side mid-session (sidecar crash/restart).
  // Previously this surfaced as a silent normal stop — frozen video, no
  // explanation. Tell the user what happened and that a retry is expected to work.
  if (rawMsg === 'server_error') {
    return { displayMsg: 'The stream ended unexpectedly due to a server error on our side \u2014 our team has been alerted. Please retry your stream.', isAccessRestricted: false, isUnauthorized: false, isFirstFrameTimeout: false, retryable: true };
  }
  if (rawMsg === 'forbidden') {
    return { displayMsg: 'Authentication failed \u2014 please sign in again, then retry.', isAccessRestricted: false, isUnauthorized: true, isFirstFrameTimeout: false, retryable: false };
  }
  const m = (rawMsg ?? '').toLowerCase();
  const isAccessRestricted = m.includes('pending approval') || m.includes('access_restricted');
  const isFirstFrameTimeout = m.includes('inference didn') || m.includes('first_frame_timeout') || m.includes('didn\u2019t start') || m.includes("didn't start");
  const isUnauthorized = !isAccessRestricted && (m.includes('unauthorized') || m.includes('invalid or expired') || m.includes('not be fully authorized') || m.includes('not signed in'));
  const displayMsg = isAccessRestricted
    ? 'Account pending approval — join the waitlist at primateintelligence.ai'
    : isFirstFrameTimeout
      // Watchdog messages are already descriptive + actionable — show verbatim.
      ? rawMsg
      : isUnauthorized
        ? 'Authentication failed — please sign in again, then retry.'
        : (rawMsg && rawMsg !== 'Connection lost')
          ? rawMsg
          : 'Connection lost';
  // Auth/authorization problems aren't fixed by an immediate retry of the same
  // unauthenticated session — guide the user to resolve auth instead.
  const retryable = !isAccessRestricted && !isUnauthorized && !isFirstFrameTimeout;
  return { displayMsg, isAccessRestricted, isUnauthorized, isFirstFrameTimeout, retryable };
}

export function StreamErrorTray({
  errorMessage, isRetrying = false, retryAttempt = 0, isMobile = false, onRetry, onDismiss,
}: {
  errorMessage: string | null;
  isRetrying?: boolean;
  retryAttempt?: number;
  isMobile?: boolean;
  onRetry?: () => void;
  onDismiss?: () => void;
}) {
  const { displayMsg, retryable } = classifyStreamError(errorMessage ?? '');
  return (
    <div
      data-testid="stream-error-tray"
      style={{
        ...(isMobile
          ? { width: '100%', maxWidth: 400 }
          : { position: 'absolute' as const, bottom: 'calc(100% + 10px)', right: 0, width: 300 }),
        background: L.bgSurface,
        border: '1px solid rgba(220,38,38,0.3)',
        borderRadius: 14,
        padding: '18px 20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
        zIndex: 50,
      }}
    >
      {!isMobile && (
        <div style={{
          position: 'absolute', bottom: -6, right: 20,
          width: 10, height: 10,
          background: L.bgSurface,
          border: '1px solid rgba(220,38,38,0.3)',
          transform: 'rotate(45deg)',
          borderTop: 'none', borderLeft: 'none',
        }} />
      )}
      <div
        data-testid="stream-error-message"
        style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', color: '#DC2626', marginBottom: 14, lineHeight: 1.5 }}
      >
        {isRetrying ? `Reconnecting... (attempt ${retryAttempt}/2)` : displayMsg}
      </div>
      {!isRetrying && (
        <div style={{ display: 'flex', gap: 8 }}>
          {retryable && onRetry && (
            <button
              data-testid="stream-retry-button"
              onClick={onRetry}
              style={{
                padding: '6px 12px', borderRadius: 8,
                border: '1px solid rgba(220,38,38,0.4)',
                background: 'rgba(220,38,38,0.08)',
                cursor: 'pointer', fontSize: 12,
                color: '#DC2626', fontFamily: 'Inter, -apple-system, sans-serif',
              }}
            >
              Retry
            </button>
          )}
          {onDismiss && (
            <button
              data-testid="stream-error-dismiss"
              onClick={onDismiss}
              style={{
                padding: '6px 12px', borderRadius: 8,
                border: `1px solid ${L.border}`,
                background: 'transparent',
                cursor: 'pointer', fontSize: 12,
                color: L.textMuted, fontFamily: 'Inter, -apple-system, sans-serif',
              }}
            >
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
}
